import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ChevronLeft, Download, Upload } from 'lucide-react'
import { useProjectStore } from '@/store/projectStore'
import { refreshJourney } from '@/store/journeyStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { useJourneyAction } from '@/lib/journeyActions'
import { getFrsContent, getJsonRaw, saveJson } from '@/api/generateapp'
import {
  createProjectRepository, getProjectReferences, pushToRepository, setProjectReferences,
} from '@/api/workbench'
import ReferenceProjectPicker from '@/components/common/ReferenceProjectPicker'
import type { RawAppData, SCMInfo } from '@/types'
import AnalyticsTab from '../AnalyticsTab'
import IntegrationTab from '../IntegrationTab'
import VersionsPage from '../VersionsPage'
import SupportTab from '../SupportTab'
import ProjectVariablesPanel from '../ProjectVariablesPanel'
import SignInRulesPanel from '../settings/SignInRulesPanel'
import { advancedItemForSub } from './advanced'

/**
 * Advanced › <tool> — today's workbench components mounted unchanged under
 * /workbench/:project/advanced/<sub> (plan §10). The Analytics workbench, the
 * Support module, Events & APIs and Source control are the same components
 * that used to be top-level tabs; the rest are the pieces the old Overview and
 * Preview tabs carried (specification, variables, linked apps, the app file).
 */
export default function AdvancedSub({ sub }: { sub: string }) {
  const run = useJourneyAction()
  const item = advancedItemForSub(sub)
  // These four were whole tabs and lay themselves out full-width.
  const fullBleed = sub === 'analytics' || sub === 'support' || sub === 'integration' || sub === 'source-control'

  const body = (() => {
    switch (sub) {
      case 'analytics': return <AnalyticsTab />
      case 'support': return <SupportTab />
      case 'integration': return <IntegrationTab />
      case 'source-control': return <><RepositoryCard /><VersionsPage /></>
      case 'specification': return <SpecificationView />
      case 'variables': return <VariablesView />
      case 'linked-apps': return <LinkedAppsView />
      case 'app-file': return <AppFileView />
      case 'signin-rules': return <div className="swb-card swb-card-pad"><SignInRulesPanel /></div>
      default: return <p className="swb-muted">There is no Advanced tool called “{sub}”.</p>
    }
  })()

  return (
    <div className={`swb ${fullBleed ? '' : 'swb-page'}`} style={fullBleed ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 12 } : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={() => void run('tab:advanced')}>
          <ChevronLeft size={14} /> Advanced
        </button>
        {item && <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{item.title}</h1>}
      </div>
      {body}
    </div>
  )
}

/* ── Specification editor (the old Specification tab) ── */
function SpecificationView() {
  const navigate = useNavigate()
  const { currentProject } = useProjectStore()
  const folder = currentProject?.application.folder_name || ''
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!folder) return
    setLoading(true)
    getFrsContent(folder)
      .then((d) => setContent(d.content || ''))
      .catch(() => setContent(null))
      .finally(() => setLoading(false))
  }, [folder])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 className="swb-section-title">Functional Requirement Specification</h2>
        <button type="button" className="swb-btn swb-btn-secondary"
          onClick={() => navigate(`/generateapp/finetune?project=${encodeURIComponent(folder)}`)}>
          Edit Specification
        </button>
      </div>
      {loading ? <div className="spinner" style={{ margin: '2rem auto' }} />
        : content ? (
          <div className="swb-card markdown-content" style={{ padding: '1.5rem' }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                strong: ({ children, ...props }) => {
                  const text = typeof children === 'string' ? children
                    : Array.isArray(children) ? children.map((c) => (typeof c === 'string' ? c : '')).join('') : ''
                  if (text && /^\[ref:\s*.+\]$/.test(text)) {
                    return <span className="swb-chip swb-chip-checked" title="Cross-application reference">{text}</span>
                  }
                  return <strong {...props}>{children}</strong>
                },
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="swb-card swb-card-pad" style={{ textAlign: 'center' }}>
            <p className="swb-muted">No specification generated yet.</p>
            <button type="button" className="swb-btn swb-btn-primary" onClick={() => navigate('/generateapp')}>Generate Specification</button>
          </div>
        )}
    </div>
  )
}

/* ── Variables (the old Overview "Variables" modal) ── */
function VariablesView() {
  const perms = useProjectPermissions()
  return (
    <div className="swb-card swb-card-pad">
      <ProjectVariablesPanel canEdit={perms.canUpdateProperties} />
    </div>
  )
}

/* ── Linked apps (the old Overview "Cross-Application References") ── */
function LinkedAppsView() {
  const { currentProject } = useProjectStore()
  const folder = currentProject?.application.folder_name || ''
  const [selected, setSelected] = useState<Array<{ folder_name: string; owner_user_id: string }>>([])

  useEffect(() => {
    if (!folder) return
    getProjectReferences(folder)
      .then((refs) => setSelected(refs.map((r) => ({ folder_name: r.folder_name, owner_user_id: r.owner_user_id }))))
      .catch(() => setSelected([]))
  }, [folder])

  const onChange = useCallback(async (refs: Array<{ folder_name: string; owner_user_id: string }>) => {
    if (!folder) return
    setSelected(refs)
    try {
      await setProjectReferences(folder, refs)
    } catch {
      toast.error('Failed to save references')
    }
  }, [folder])

  return <ReferenceProjectPicker projectId={folder} value={selected} onChange={onChange} />
}

/* ── Repository summary + Push / Create (the old Overview buttons) ── */
function RepositoryCard() {
  const { currentProject, scmInfo, fetchScmInfo } = useProjectStore()
  const perms = useProjectPermissions()
  const folder = currentProject?.application.folder_name || ''
  const [pushing, setPushing] = useState(false)
  const [creating, setCreating] = useState(false)
  useEffect(() => { if (folder) fetchScmInfo(folder) }, [folder, fetchScmInfo])
  const info: SCMInfo | null = scmInfo
  if (!info) return null
  const isDraft = folder === 'Draft Application'
  const fail = (err: unknown, fallback: string) =>
    toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback)

  return (
    <section className="swb-card swb-card-pad" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
      <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 2, fontSize: 14 }}>
        {info.has_repository ? (
          <>
            <strong>Repository connected{info.branch_name ? ` · ${info.branch_name}` : ''}</strong>
            {info.repository_url && <a href={info.repository_url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{info.repository_url}</a>}
            <span className="swb-muted">
              {info.last_pushed_at ? `Last pushed ${info.last_pushed_at}` : 'Not pushed yet'}
              {info.files_changed ? ` · ${info.files_changed} file${info.files_changed !== 1 ? 's' : ''} changed` : ''}
            </span>
          </>
        ) : (
          <span className="swb-muted">
            {info.scm_enabled ? 'No repository has been created for this project yet.'
              : 'Source control is not enabled. Configure SCM in Settings to enable Git integration.'}
          </span>
        )}
      </span>
      {info.has_repository && perms.canDeploy && (
        <button type="button" className="swb-btn swb-btn-secondary" disabled={pushing} onClick={async () => {
          setPushing(true)
          try {
            const r = await pushToRepository(folder)
            toast.success(r.message || 'Changes pushed to repository')
            fetchScmInfo(folder)
          } catch (err) { fail(err, 'Failed to push') } finally { setPushing(false) }
        }}>{pushing ? 'Pushing…' : 'Push to Git'}</button>
      )}
      {!info.has_repository && info.scm_enabled && !isDraft && perms.canDeploy && (
        <button type="button" className="swb-btn swb-btn-primary" disabled={creating} onClick={async () => {
          setCreating(true)
          try {
            const r = await createProjectRepository(folder)
            toast.success(r.message || 'Repository created')
            fetchScmInfo(folder)
          } catch (err) { fail(err, 'Failed to create repository') } finally { setCreating(false) }
        }}>{creating ? 'Creating…' : 'Create Repository'}</button>
      )}
    </section>
  )
}

/* ── App file (the old Preview tab's Upload / Download JSON) ── */
function AppFileView() {
  const { currentProject } = useProjectStore()
  const perms = useProjectPermissions()
  const folder = currentProject?.application.folder_name || ''
  const fileRef = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<RawAppData | null>(null)
  const [saving, setSaving] = useState(false)

  const download = async () => {
    try {
      const data = await getJsonRaw(folder)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${folder || 'application'}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[app-file] download failed', err)
      toast.error('Could not download the app file')
    }
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string) as RawAppData
        if (data.application && Array.isArray(data.navigation)) setPending(data)
        else toast.error('Invalid application JSON format')
      } catch {
        toast.error('Failed to parse JSON file')
      }
    }
    reader.onerror = () => toast.error('Could not read the file')
    reader.readAsText(file)
  }

  const replace = async () => {
    if (!pending) return
    setSaving(true)
    try {
      await saveJson(folder, pending)
      toast.success('App file replaced')
      setPending(null)
      refreshJourney()
      window.dispatchEvent(new CustomEvent('twasta:agent-files-changed', { detail: { project: folder, files: ['application.json'] } }))
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail ? detail : 'Failed to save application')
    } finally {
      setSaving(false)
    }
  }

  const canEdit = !perms.loaded || perms.canUpdateNavigation || perms.canUpdateProperties
  return (
    <div className="swb-card swb-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0 }}>
        The application JSON holds the app’s settings and its menu. Download it to keep a copy, or upload
        one to replace them.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="swb-btn swb-btn-secondary" onClick={() => void download()}><Download size={15} /> Download JSON</button>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".json" onChange={onFile} style={{ display: 'none' }} />
            <button type="button" className="swb-btn swb-btn-secondary" onClick={() => fileRef.current?.click()}><Upload size={15} /> Upload JSON</button>
          </>
        )}
      </div>
      {pending && (
        <div className="swb-notice swb-notice-warn">
          <span style={{ flex: '1 1 300px', fontSize: 14 }}>
            <strong>{pending.application.title || pending.application.id}</strong> — {pending.navigation.length} menu
            items. Replacing overwrites the current settings and menu.
          </span>
          <button type="button" className="swb-btn swb-btn-secondary" onClick={() => setPending(null)} disabled={saving}>Cancel</button>
          <button type="button" className="swb-btn swb-btn-primary" onClick={() => void replace()} disabled={saving}>
            {saving ? 'Saving…' : 'Replace the app file'}
          </button>
        </div>
      )}
    </div>
  )
}
